import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Car } from '../types/Car';
import { ArrowLeft } from 'lucide-react';
import CarGallery from '../components/car/CarGallery';
import PriceSidebar from '../components/car/PriceSidebar';

export default function CarDetails() {
  const { id } = useParams<{ id: string }>();
  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(true);

  const veh_price = car?.veh_price || 0;



const formatINR = (n: number) =>
  Math.round(n).toLocaleString("en-IN");

  useEffect(() => {
    const fetchCar = async () => {
      if (!id) return;
      try {
        const docRef = doc(db, 'cars', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const d = docSnap.data();
          setCar({
            id: docSnap.id,
            name: d.name || `${d.make || ''} ${d.model || ''}`.trim(),
            make: d.make || '',
            model: d.model || '',
            year: d.year || '',
            veh_price: typeof d.veh_price === 'string'
            ? parseInt(d.veh_price.replace(/[^0-9]/g, '')) || 0
            : (typeof d.veh_price === 'number' ? d.veh_price : 0),
            img: Array.isArray(d.img) ? d.img : [d.img || 'https://via.placeholder.com/800x500'],
            fuel: d.veh_fuel_type || 'Petrol',
            transmission: d.veh_transmission_type || 'Manual',
            kmsDriven: d.veh_kms_driven || '0',
            ownership: d.veh_ownership || 'First',
            reg_number: d.reg_number || '',
            veh_engine_capacity: d.veh_engine_capacity || '',
            veh_insurance: d.veh_insurance || '',
          });
        }
      } catch (e) {
        console.error('Error fetching car:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCar();
  }, [id]);
  const loaderTextStyle = {
    position: 'absolute' as const,
    fontSize: '10px',
    color: '#FFD700',
    marginTop: '60px',
  };
  
  if (loading)
    return (
      <div style={fullCenter}>
        <div className="loader">
          <span style={loaderTextStyle}>PRG Cars</span>
        </div>
      </div>
    );
  if (!car) return <div style={fullCenter}>Car not found</div>;

  const formattedPrice = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(car.veh_price || 0));

  const kmFormatted =
    Number(car.kmsDriven) > 0
      ? Number(car.kmsDriven).toLocaleString('en-IN') + ' km'
      : car.kmsDriven + ' km';

  return (
    <div style={page}>

      {/* ── NAV ── */}
      <div style={navBar}>
        <Link to="/" style={backLink}>
          <ArrowLeft size={14} />
          <span>All Listings</span>
        </Link>
        <span style={breadcrumb}>{car.make} / {car.model}</span>
      </div>

      {/* ── GALLERY BAND (full-width bg, gallery centered at 60%) ── */}
      <div style={galleryBand}>
        {/* On desktop the gallery is 60% wide and centered; on mobile full width */}
        <div className="gallery-sizer" style={galleryOuter}>
          <CarGallery images={car.img} carName={car.name} />
        </div>
      </div>

      {/* ── TITLE + PRICE ── */}

      <div style={titleBand}>
        <div style={titleInner}>
          <div>
            <h1 style={titleText}>{car.year} {car.name}</h1>
            
            <div style={locationWrap}>
              <LocationIcon />
              <span>Available at Manish Nagar</span>
            </div>
          </div>
        </div>
      </div>

      {/* Shows only on mobile — hidden on desktop via .mobile-emi CSS rule */}
        <div className="mobile-emi">
          <PriceSidebar car={car} />
        </div>

      {/* ── STATS STRIP ── */}
      <div style={statsStrip}>
        <div style={statsInner} className="stats-inner">
          {statItem(<StarSVG />,        'Ownership',     car.ownership ? `${car.ownership} Owner` : '—')}
          <div style={statDivider} />
          {statItem(<ClockSVG />,       'KMs Driven',    kmFormatted)}
          <div style={statDivider} />
          {statItem(<FuelBarSVG />,     'Fuel',          car.fuel)}
          <div style={statDivider} />
          {statItem(<GearboxSVG />,     'Transmission',  car.transmission)}
        </div>
      </div>

      {/* ── MAIN CONTENT + SIDEBAR ── */}
      <div style={contentWrap} className="content-wrap">

        {/* LEFT */}
        <div style={leftCol}>

          {/* Vehicle Details */}
          <section style={card}>
            <h2 style={cardTitle}>
              Vehicle Details
              <span style={cardTitleRule} />
            </h2>
            <div style={specGrid}>
              {car.make              && specCell('Make',        car.make,                <CarSVG color="#378ADD" />,    '#111820', '#192230')}
              {car.model             && specCell('Model',       car.model,               <DocSVG color="#1D9E75" />,    '#0e1a14', '#1a2e20')}
              {car.year              && specCell('Year',        String(car.year),         <CalSVG color="#BA7517" />,    '#1a1500', '#2e2500')}
              {car.fuel              && specCell('Fuel Type',   car.fuel,                <PumpSVG color="#1D9E75" />,   '#0e1a14', '#1a2e20')}
              {car.transmission      && specCell('Gearbox',     car.transmission,         <GearSVG color="#378ADD" />,   '#111820', '#192230')}
              {car.ownership         && specCell('Ownership',   `${car.ownership} Owner`, <UserSVG color="#BA7517" />,   '#1a1500', '#2e2500')}
              {car.veh_engine_capacity && specCell('Engine',    car.veh_engine_capacity,  <EngineSVG color="#D4537E" />, '#1a1018', '#2a1a28')}
              {car.veh_insurance     && specCell('Insurance',   car.veh_insurance,        <ShieldSVG color="#1D9E75" />, '#0e1a14', '#1a2e20')}
              {car.reg_number        && specCellWide('Reg. Number', car.reg_number,       <PlateSVG />)}
            </div>
          </section>

          {/* Key Highlights */}
          <section style={card}>
            <h2 style={cardTitle}>
              Key Highlights
              <span style={cardTitleRule} />
            </h2>
            <div style={highlightGrid}>
              <div style={highlightItem}>
                <div style={{ ...hlIconWrap, background: '#0e1a14', border: '1px solid #1a2e20' }}>
                  <CheckCircleSVG color="#1D9E75" />
                </div>
                <div>
                  <div style={hlLabel}>Accident Free</div>
                  <div style={hlValue}>No incidents reported</div>
                </div>
              </div>
              <div style={highlightItem}>
                <div style={{ ...hlIconWrap, background: '#1a1500', border: '1px solid #2e2500' }}>
                  <ChartBarSVG color="#BA7517" />
                </div>
                <div>
                  <div style={hlLabel}>Service Records</div>
                  <div style={hlValue}>All records available</div>
                </div>
              </div>
              <div style={highlightItem}>
                <div style={{ ...hlIconWrap, background: '#111820', border: '1px solid #192230' }}>
                  <ShieldSVG color="#378ADD" />
                </div>
                <div>
                  <div style={hlLabel}>Insurance</div>
                  <div style={hlValue}>{car.veh_insurance || 'Comprehensive'}</div>
                </div>
              </div>
              <div style={highlightItem}>
                <div style={{ ...hlIconWrap, background: '#1a1018', border: '1px solid #2a1a28' }}>
                  <UserSVG color="#D4537E" />
                </div>
                <div>
                  <div style={hlLabel}>Ownership</div>
                  <div style={hlValue}>{car.ownership ? `${car.ownership} Owner` : 'First Owner'}</div>
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* RIGHT: Sidebar */}
        <aside style={rightCol} className="right-col">
          <div style={sidebarSticky}>
            <PriceSidebar car={car} />
          </div>
        </aside>
      </div>

      <style>{`
        * { box-sizing: border-box; }

        @media (min-width: 1024px) {
          .content-wrap       { grid-template-columns: 1fr 340px !important; }
          .mobile-cta         { display: none !important; }
          .gallery-sizer      { max-width: 60% !important; margin: 0 auto !important; }
          .mobile-emi         { display: none !important; }   /* 👈 hide on desktop */
        }
      
        @media (max-width: 1023px) {
          .right-col          { display: none !important; }   /* 👈 hide sidebar on mobile */
          .stats-inner        { flex-wrap: wrap; }
          .gallery-sizer      { max-width: 100% !important; }
        }
      
        a:hover { opacity: 0.75; }
      `}</style>
    </div>
  );
}

/* ── INLINE SVG ICONS ── */

const svgProps = { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none' } as const;

function StarSVG() {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      <path d="M9 2L10.8 6.6L16 7.2L12.4 10.6L13.4 16L9 13.4L4.6 16L5.6 10.6L2 7.2L7.2 6.6Z"
        stroke="#FFD700" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}
function ClockSVG() {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="6" stroke="#FFD700" strokeWidth="1.2" />
      <path d="M9 5V9L12 11" stroke="#FFD700" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function FuelBarSVG() {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      <path d="M5 14V8M9 14V5M13 14V10" stroke="#FFD700" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function GearboxSVG() {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      <rect x="2" y="6" width="14" height="8" rx="2" stroke="#FFD700" strokeWidth="1.2" />
      <path d="M5 6V4.5C5 3.7 5.7 3 6.5 3H11.5C12.3 3 13 3.7 13 4.5V6" stroke="#FFD700" strokeWidth="1.2" />
      <path d="M7 10H11" stroke="#FFD700" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function CarSVG({ color }: { color: string }) {
  return (
    <svg {...svgProps}>
      <path d="M2 10L4 5H12L14 10" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1 10H15" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="4" cy="11.5" r="1.5" fill={color} />
      <circle cx="12" cy="11.5" r="1.5" fill={color} />
    </svg>
  );
}
function DocSVG({ color }: { color: string }) {
  return (
    <svg {...svgProps}>
      <rect x="2" y="3" width="12" height="10" rx="2" stroke={color} strokeWidth="1.2" />
      <path d="M5 7H11M5 9.5H8" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function CalSVG({ color }: { color: string }) {
  return (
    <svg {...svgProps}>
      <rect x="2" y="3" width="12" height="11" rx="2" stroke={color} strokeWidth="1.2" />
      <path d="M2 7H14M5.5 2V4M10.5 2V4" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function PumpSVG({ color }: { color: string }) {
  return (
    <svg {...svgProps}>
      <path d="M4 14V6C4 5.4 4.4 5 5 5H9C9.6 5 10 5.4 10 6V14" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
      <path d="M3 14H11M10 7L12 5.5C12.5 5.1 13 4.6 13 4V3" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
      <path d="M6 8H8" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function GearSVG({ color }: { color: string }) {
  return (
    <svg {...svgProps}>
      <circle cx="4" cy="4.5" r="1.5" stroke={color} strokeWidth="1.2" />
      <circle cx="8" cy="4.5" r="1.5" stroke={color} strokeWidth="1.2" />
      <circle cx="4" cy="11.5" r="1.5" stroke={color} strokeWidth="1.2" />
      <circle cx="8" cy="8" r="1.5" stroke={color} strokeWidth="1.2" />
      <circle cx="12" cy="4.5" r="1.5" stroke={color} strokeWidth="1.2" />
      <path d="M8 6V8" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function UserSVG({ color }: { color: string }) {
  return (
    <svg {...svgProps}>
      <circle cx="8" cy="5.5" r="2.5" stroke={color} strokeWidth="1.2" />
      <path d="M3 14C3 11.2 5.2 9 8 9C10.8 9 13 11.2 13 14" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function EngineSVG({ color }: { color: string }) {
  return (
    <svg {...svgProps}>
      <rect x="3" y="5" width="10" height="7" rx="1.5" stroke={color} strokeWidth="1.2" />
      <path d="M5 5V3.5M8 5V3M11 5V3.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
      <path d="M3 8.5H1.5M14.5 8.5H13" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function ShieldSVG({ color }: { color: string }) {
  return (
    <svg {...svgProps}>
      <path d="M8 2L13 4V8C13 11 10.5 13.5 8 14C5.5 13.5 3 11 3 8V4L8 2Z"
        stroke={color} strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M6 8L7.5 9.5L10.5 6.5" stroke={color} strokeWidth="1.2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function PlateSVG() {
  return (
    <svg {...svgProps}>
      <rect x="2" y="4" width="12" height="8" rx="2" stroke="#888" strokeWidth="1.2" />
      <path d="M4.5 8H6.5M11 7V9" stroke="#888" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="9" cy="8" r="0.8" fill="#888" />
    </svg>
  );
}
function CheckCircleSVG({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="6" stroke={color} strokeWidth="1.2" />
      <path d="M6.5 9L8 10.5L11.5 7" stroke={color} strokeWidth="1.4"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ChartBarSVG({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      <path d="M4 14V7M9 14V4M14 14V9" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

/* ── HELPERS ── */

function statItem(icon: React.ReactNode, label: string, value: string) {
  return (
    <div style={statCell}>
      <div style={statIconWrap}>{icon}</div>
      <div>
        <div style={statLabel}>{label}</div>
        <div style={statValue}>{value}</div>
      </div>
    </div>
  );
}

function specCell(
  label: string, value: string,
  icon: React.ReactNode,
  iconBg: string, iconBorder: string,
) {
  return (
    <div style={specItem} key={label}>
      <div style={{ ...specIconWrap, background: iconBg, border: `1px solid ${iconBorder}` }}>
        {icon}
      </div>
      <div>
        <div style={specLabel}>{label}</div>
        <div style={specValue}>{value}</div>
      </div>
    </div>
  );
}

function specCellWide(label: string, value: string, icon: React.ReactNode) {
  return (
    <div style={{ ...specItem, gridColumn: 'span 2' }} key={label}>
      <div style={{ ...specIconWrap, background: '#1a1a1a', border: '1px solid #2e2e2e' }}>
        {icon}
      </div>
      <div>
        <div style={specLabel}>{label}</div>
        <div style={{ ...specValue, fontFamily: 'monospace', letterSpacing: '.04em', color: '#aaa' }}>{value}</div>
      </div>
    </div>
  );
}

function LocationIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2">
      <path d="M12 21s-6-4.5-6-10a6 6 0 1 1 12 0c0 5.5-6 10-6 10z" />
      <circle cx="12" cy="11" r="2.5" />
    </svg>
  );
}

const locationWrap = {
  marginTop: '8px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '12px',
  color: '#FFD700',
  background: '#111',
  border: '1px solid #222',
  padding: '4px 10px',
  borderRadius: '20px',
};


/* ── STYLES ── */

const page        : React.CSSProperties = { background: '#080808', minHeight: '100vh', color: '#fff' };
const fullCenter  : React.CSSProperties = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#080808', color: '#fff' };

/* Nav */
const navBar      : React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: '#0d0d0d', borderBottom: '1px solid #1a1a1a' };
const backLink    : React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '6px', color: '#888', textDecoration: 'none', fontSize: '13px' };
const breadcrumb  : React.CSSProperties = { fontSize: '11px', color: '#3a3a3a', letterSpacing: '.06em', textTransform: 'uppercase' };

/* Gallery band — full-width dark strip, gallery centered inside */
const galleryBand : React.CSSProperties = { width: '100%', background: '#080808', display: 'flex', justifyContent: 'center', borderBottom: '1px solid #1a1a1a' };
const galleryOuter: React.CSSProperties = { width: '100%' }; // width controlled by .gallery-sizer media query

/* Title */
const titleBand   : React.CSSProperties = { background: '#0d0d0d', borderBottom: '1px solid #1a1a1a', padding: '16px 24px' };
const titleInner  : React.CSSProperties = { maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' };
const titleText   : React.CSSProperties = { fontSize: 'clamp(18px, 3vw, 24px)', fontWeight: 700, margin: 0, letterSpacing: '-.02em', color: '#f0f0f0' };
const regChip     : React.CSSProperties = { background: '#111', border: '1px solid #222', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', color: '#666', fontFamily: 'monospace', letterSpacing: '.04em' };
const mobilePriceBadge: React.CSSProperties = { background: '#FFD700', color: '#000', fontWeight: 800, fontSize: '16px', padding: '8px 18px', borderRadius: '8px', whiteSpace: 'nowrap', flexShrink: 0 };

/* Stats strip */
const statsStrip  : React.CSSProperties = { background: '#0a0a0a', borderBottom: '1px solid #181818' };
const statsInner  : React.CSSProperties = { maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'stretch' };
const statCell    : React.CSSProperties = { flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px' };
const statDivider : React.CSSProperties = { width: '1px', background: '#1a1a1a', flexShrink: 0 };
const statIconWrap: React.CSSProperties = { width: '38px', height: '38px', borderRadius: '10px', background: '#131313', border: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const statLabel   : React.CSSProperties = { fontSize: '10px', color: '#444', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: '3px' };
const statValue   : React.CSSProperties = { fontSize: '13px', fontWeight: 600, color: '#d0d0d0' };

/* Content */
const contentWrap : React.CSSProperties = { maxWidth: '1200px', margin: '0 auto', padding: '24px 20px 20px', display: 'grid', gridTemplateColumns: '1fr', gap: '24px' };
const leftCol     : React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '16px' };
const rightCol    : React.CSSProperties = {};
const sidebarSticky: React.CSSProperties = { position: 'sticky', top: '80px' };

/* Cards */
const card        : React.CSSProperties = { background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: '12px', padding: '22px' };
const cardTitle   : React.CSSProperties = { fontSize: '11px', fontWeight: 600, color: '#555', letterSpacing: '.08em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 18px' };
const cardTitleRule: React.CSSProperties = { flex: 1, height: '1px', background: '#1a1a1a', display: 'block' };

/* Spec grid */
const specGrid    : React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' };
const specItem    : React.CSSProperties = { background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: '10px' };
const specIconWrap: React.CSSProperties = { width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' };
const specLabel   : React.CSSProperties = { fontSize: '10px', color: '#444', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '3px' };
const specValue   : React.CSSProperties = { fontSize: '13px', color: '#ddd', fontWeight: 500 };

/* Highlights */
const highlightGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' };
const highlightItem: React.CSSProperties = { background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: '10px', padding: '14px', display: 'flex', alignItems: 'center', gap: '12px' };
const hlIconWrap   : React.CSSProperties = { width: '36px', height: '36px', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const hlLabel      : React.CSSProperties = { fontSize: '10px', color: '#444', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: '3px' };
const hlValue      : React.CSSProperties = { fontSize: '13px', color: '#ddd', fontWeight: 500 };

/* Mobile CTA */
const mobileCta    : React.CSSProperties = { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#0d0d0d', borderTop: '1px solid #1a1a1a', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', zIndex: 100 };
const mobileCtaPrice: React.CSSProperties = { fontSize: '18px', fontWeight: 700, color: '#FFD700' };
const mobileCtaBtn  : React.CSSProperties = { background: '#FFD700', color: '#000', border: 'none', borderRadius: '8px', padding: '12px 24px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', width:'90%' , margin:'Auto' };

